/**
 * The popup -> frame session handshake used by the embedded RMS view.
 *
 * An embedded frame cannot sign a user in itself: SSO and Google both navigate
 * the window to a third party (Stytch, then the customer's IdP), and neither
 * will render inside a frame. So the frame opens a *top-level* popup, the
 * ordinary sign-in flow runs there unmodified, and the popup hands the
 * resulting session back to the frame by postMessage before closing.
 *
 * Both windows are Finpilot's own origin. The popup targets that origin
 * explicitly, and the frame checks `event.origin` on receipt — so the embedding
 * host page, being a different origin, can neither read the session nor inject
 * one of its own. That origin check is the whole security property; do not
 * relax it to `"*"`.
 *
 * See #11021.
 */

export const EMBED_SESSION_MESSAGE_TYPE = "finpilot:embed-session";

interface EmbedSessionMessage {
  type: typeof EMBED_SESSION_MESSAGE_TYPE;
  token: string;
}

/**
 * Extract a session token from a `message` event, or null if the event is not a
 * session handover we should trust.
 *
 * Rejects: any other origin, any other message type (the page receives
 * unrelated same-origin messages from tooling and third-party scripts), and any
 * payload whose token is missing or not a string.
 */
export function readSessionMessage(
  event: MessageEvent<unknown>,
  expectedOrigin: string
): string | null {
  if (event.origin !== expectedOrigin) return null;

  const data = event.data;
  if (typeof data !== "object" || data === null) return null;

  const message = data as Partial<EmbedSessionMessage>;
  if (message.type !== EMBED_SESSION_MESSAGE_TYPE) return null;
  if (typeof message.token !== "string" || message.token === "") return null;

  return message.token;
}

/**
 * Called by the popup once sign-in has completed: hand the session to the frame
 * that opened us, then close.
 *
 * Returns false when there is no opener — someone reached this URL directly
 * rather than through the embed flow — so the caller can show something other
 * than a window that closes itself.
 */
export function postSessionToOpener(token: string): boolean {
  const opener = window.opener as Window | null;
  if (!opener || opener.closed) return false;

  const message: EmbedSessionMessage = {
    type: EMBED_SESSION_MESSAGE_TYPE,
    token,
  };
  // Targeted at our own origin, never "*": the frame is same-origin with this
  // popup, and anything broader would hand the session to the embedding host.
  opener.postMessage(message, window.location.origin);
  return true;
}
